/**
 * Resource-node in-world placement (E3.4.S3) — consume the join-only
 * `helloAck.nodes` the client rendered nowhere and stand each node at its (x, z).
 * Nodes are join-only zone state whose depletion/respawn arrive as discrete
 * GameEvents (`messages.ts` `NodeWire`), NEVER on the per-tick snapshot path, so
 * this is a PLACER in the `NpcPlacer` mould rather than an upgrade-in-place
 * renderer: `place()` the zone's nodes on join, `clear()`/rebuild on a zone
 * change, `dispose()` on teardown.
 *
 * **Unlike NPCs, an unresolvable node is NOT nothing.** The NPC placer adds no
 * primitive when a resolve returns null, because an NPC that renders nothing is
 * the honest pack-absence baseline. A resource node is different in kind: it is
 * INTERACTABLE (`interactIntent` carries its `nodeId`) and it is already on the
 * HUD minimap, so a node the player can harvest and read on the map but cannot
 * see in the world is the same class of defect E3.4.S2 cured for settlement
 * pieces — a thing the server treats as present and the client draws as absent.
 * So every node gets a body: its archetype mesh when one resolves, else the
 * injected placeholder.
 *
 * The placeholder is INJECTED (`makePlaceholder`) rather than built here, which
 * is what keeps this module node-safe: the only `three` dependency is the
 * `Object3D` TYPE (erased at runtime), the loader is injected and the scene is an
 * abstract `NodeScene`, so the whole lifecycle runs under `node --test` with fakes
 * (AD-120). A caller that injects no factory gets the resolved-only behaviour and
 * the miss is COUNTED, never silent.
 *
 * Graceful contract, in the sibling placers' terms:
 *   - resolve `null` (undelivered resource, no archetype, ambiguous kit) ⇒ placeholder,
 *   - unknown model / null template / load error ⇒ placeholder (the loader's own null path),
 *   - a load settling after a `clear()`/re-`place()` ⇒ drop the clone, no orphan
 *     (a placer-wide monotonic generation guard),
 *   - never a throw into the frame loop.
 *
 * Nothing rides the per-tick path — placement keys off the join ack (AD-031) and
 * the node meshes are bodyless render objects carrying no collision (the world
 * descriptor is untouched). Import boundary (AD-122): shared types via
 * `@great-cloud/shared/client`.
 */

import type { Object3D } from 'three';

import type { AssetPackDef, NodeWire } from '@great-cloud/shared/client';

import { PackLoaderCache, disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import { resolveNode, type NodeContent, type NodeResolution } from './resolve.js';
import { markWorldContent } from '../scene/worldContent.js';
import { pointsAnchor, type PendingAssetSink } from '../pendingAssets.js';

/**
 * The minimal scene slice the placer adds/removes node roots on — a `THREE.Scene`
 * structurally. Abstract so the lifecycle runs under `node --test` with a fake.
 */
export interface NodeScene {
  add(object: Object3D): void;
  remove(object: Object3D): void;
}

/** Everything App hands Game so it can place resource nodes. */
export interface NodeBridge {
  content: NodeContent;
}

export interface NodePlacerOptions {
  /** Presentation lookup (resource defs for the archetype hop + delivered packs). */
  content: NodeContent;
  /** The scene node roots are added to (`Game`'s `THREE.Scene`). */
  scene: NodeScene;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /**
   * Builds the placeholder body for a node whose archetype mesh cannot be
   * delivered. Injected so this module stays `three`-free; omitted ⇒ no
   * placeholder is added and the miss is counted in {@link placeholderCount}.
   */
  makePlaceholder?: () => Object3D;
  /** GPU-resource disposer for a removed mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * AD-408: the visual ground height at a world point — the zone's heightfield
   * surface, read when a body settles. Absent ⇒ flat ground (y = 0, today).
   * Render-only: the node's (x, z) is the wire's, untouched.
   */
  groundHeightAt?: (x: number, z: number) => number;
  /**
   * sprint-assembly-1 (AD-647): the ground ring that marks a node as GATHERABLE, so a
   * timber node reads differently from the scatter trees around it. Given the node's
   * resolved archetype (null when unresolved) to size itself. Omit ⇒ no rings.
   */
  makeHighlight?: (archetype: string | null) => Object3D | null;
  /** AD-665: draw a gather channel's progress (0..1, or null to clear) on a node's ring. */
  setHighlightProgress?: (highlight: Object3D, frac: number | null) => void;
  /**
   * Full detail places the TEXTURED base artifact (falling back to LOD0), as the
   * creature/equipment renderers do; low quality keeps the procedural LOD0. Every
   * node kit's base is authored to its LOD0 footprint (ground at y 0, identity
   * root), so it drops in unseated. `Game` passes `fullDetailAssetsActive()`; it is
   * injected, not imported, so this module stays `three`-free. Default `false` (LOD0).
   * (q-flora-node-lods-untextured: LOD0-only nodes drew flat beside textured scatter.)
   * A FUNCTION decides per resolved pack (`q-flora-low-tier-textures`: `Game` passes
   * `packBaseAssetsActive`, so a kit's `lowQualityBase` opts its base into low quality).
   */
  useFullDetailBase?: boolean | ((pack: AssetPackDef) => boolean);
  /**
   * Where in-flight loads are reported (q-asset-readiness-loading-screen), anchored where
   * they will appear, so the loading screen holds until those in view have arrived.
   */
  pending?: PendingAssetSink;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[nodes] ${message}`, error ?? '');
}

/**
 * Places a zone's resource nodes. Create it once against the content bridge,
 * `place()` the join ack's `nodes`, and `dispose()` on scene teardown.
 */
export class NodePlacer {
  private readonly content: NodeContent;
  private readonly scene: NodeScene;
  private readonly loaders: PackLoaderCache;
  private readonly makePlaceholder: (() => Object3D) | undefined;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly groundHeightAt: (x: number, z: number) => number;
  private readonly makeHighlight: ((archetype: string | null) => Object3D | null) | undefined;
  private readonly setHighlightProgress:
    | ((highlight: Object3D, frac: number | null) => void)
    | undefined;
  private readonly useFullDetailBase: boolean | ((pack: AssetPackDef) => boolean);
  private readonly pending: PendingAssetSink | null;
  /** nodeId → its settled ring (AD-665 gather progress). */
  private readonly rings = new Map<number, Object3D>();
  /** The node whose ring currently shows progress. */
  private progressNode: number | null = null;
  /**
   * Server-authoritative depletion by node id (join ack `depleted` + `nodeDepleted` /
   * `nodeRespawned` events). A depleted node shows NO ring — owner 2026-10-24.
   */
  private readonly depleted = new Set<number>();

  /** The settled node roots (and their highlight rings) currently in the scene. */
  private placed: Object3D[] = [];
  /** Bumped on every `place()`/`clear()`; a load settling at a stale value is dropped. */
  private generation = 0;
  private disposed = false;
  /** How many nodes of the last `place()` fell back to the placeholder body. */
  private placeholders = 0;
  /** How many nodes of the last `place()` got their resolved archetype mesh. */
  private resolved = 0;

  constructor(options: NodePlacerOptions) {
    this.content = options.content;
    this.scene = options.scene;
    this.loaders = new PackLoaderCache({
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      onWarn: options.onWarn ?? defaultWarn,
    });
    this.makePlaceholder = options.makePlaceholder;
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.groundHeightAt = options.groundHeightAt ?? (() => 0);
    this.makeHighlight = options.makeHighlight;
    this.setHighlightProgress = options.setHighlightProgress;
    this.useFullDetailBase = options.useFullDetailBase ?? false;
    this.pending = options.pending ?? null;
  }

  /**
   * Show the own gather channel's progress on node `nodeId`'s ring (AD-665): `frac` 0..1,
   * or `null` to clear. Moving to another node clears the previous one. Cheap per frame.
   */
  setGatherProgress(nodeId: number | null, frac: number | null): void {
    if (this.setHighlightProgress === undefined) return;
    if (this.progressNode !== null && this.progressNode !== nodeId) {
      const old = this.rings.get(this.progressNode);
      if (old) this.setHighlightProgress(old, null);
      this.progressNode = null;
    }
    if (nodeId === null) return;
    const ring = this.rings.get(nodeId);
    if (ring === undefined) return;
    this.setHighlightProgress(ring, frac);
    this.progressNode = frac === null ? null : nodeId;
  }

  /**
   * Flip a node's depleted state (server events): its ground ring is hidden while depleted
   * and shown again on respawn. Safe before the node settles (applied when its ring lands).
   */
  setDepleted(nodeId: number, depleted: boolean): void {
    if (depleted) this.depleted.add(nodeId);
    else this.depleted.delete(nodeId);
    const ring = this.rings.get(nodeId);
    if (ring !== undefined) ring.visible = !depleted;
  }

  /** Nodes of the last `place()` that fell back to a placeholder body. */
  get placeholderCount(): number {
    return this.placeholders;
  }

  /** Nodes of the last `place()` that got their resolved archetype mesh. */
  get resolvedCount(): number {
    return this.resolved;
  }

  /**
   * Place the zone's resource nodes (join-only). Clears any prior placement first
   * (a zone change re-runs this off `onReady`), then resolves + instantiates each
   * `NodeWire` asynchronously and stands its body at (x, 0, z). Safe to call before
   * the content pack loads — every node then resolves to `null` and gets a
   * placeholder; `Game` re-`place()`s once the pack arrives, exactly like the world
   * scene, and the placeholders are replaced by the real meshes.
   */
  place(nodes: readonly NodeWire[]): void {
    this.clear(); // supersedes any in-flight load + removes prior bodies
    if (this.disposed) return;
    const generation = this.generation;
    this.placeholders = 0;
    this.resolved = 0;
    for (const node of nodes) this.setDepleted(node.id, node.depleted);
    for (const node of nodes) {
      const resolution = resolveNode(this.content, node.resource);
      const load = this.instantiate(resolution);
      const anchor = pointsAnchor([
        { x: node.x, y: this.groundHeightAt(node.x, node.z), z: node.z },
      ]);
      // An unresolved node settles at once on its placeholder: only real loads are reported.
      void (
        resolution !== null && this.pending
          ? this.pending.track(`node: ${node.resource}`, anchor, load)
          : load
      )
        .then((loaded) => {
          if (generation !== this.generation || this.disposed) {
            // Superseded (zone change / dispose before settle) ⇒ dispose, no orphan.
            if (loaded !== null) this.disposeObject(loaded);
            return;
          }
          // A null here is either "nothing resolved" or "the load failed" — the
          // placeholder covers BOTH, which is why the fallback lives after the
          // await and not beside the resolve.
          const root = loaded ?? this.placeholderBody();
          if (root === null) return;
          if (loaded === null) this.placeholders += 1;
          else this.resolved += 1;
          // static placement; nodes have no facing. AD-408: seated on the ground.
          root.position.set(node.x, this.groundHeightAt(node.x, node.z), node.z);
          this.scene.add(markWorldContent(root)); // AD-705: hidden while a run is drawn
          this.placed.push(root);
          const ring = this.makeHighlight?.(resolution?.archetype ?? null) ?? null;
          if (ring !== null) {
            ring.position.set(node.x, root.position.y, node.z); // the same seat as its node
            this.scene.add(markWorldContent(ring));
            this.placed.push(ring); // cleared + disposed with the node bodies
            ring.visible = !this.depleted.has(node.id);
            this.rings.set(node.id, ring);
          }
        })
        .catch((error: unknown) => {
          // The loader degrades failures to null; guard an unexpected throw so a bad
          // placement never breaks the frame loop.
          this.onWarn(`node ${node.id} ('${node.resource}') placement failed`, error);
        });
    }
  }

  /** The injected placeholder body, or `null` when no factory was provided. */
  private placeholderBody(): Object3D | null {
    return this.makePlaceholder === undefined ? null : this.makePlaceholder();
  }

  /** Instantiate a resolution's root, or `null` (unresolved / unknown model / error). */
  private async instantiate(resolution: NodeResolution | null): Promise<Object3D | null> {
    if (resolution === null) return null;
    const preferBase =
      typeof this.useFullDetailBase === 'function'
        ? this.useFullDetailBase(resolution.pack)
        : this.useFullDetailBase;
    return this.loaders
      .loaderFor(resolution.pack)
      .instantiatePreferred(resolution.model, preferBase);
  }

  /**
   * Remove + dispose every placed node body and supersede any in-flight load (a
   * zone change re-place, or teardown). Keeps the loader TEMPLATE caches (a
   * re-place reuses them); `dispose()` drops those.
   */
  clear(): void {
    this.generation += 1;
    for (const root of this.placed) {
      this.scene.remove(root);
      this.disposeObject(root);
    }
    this.placed = [];
    this.rings.clear();
    this.depleted.clear();
    this.progressNode = null;
  }

  /** Teardown: clear placements + drop the loader caches. */
  dispose(): void {
    this.disposed = true;
    this.clear();
    this.loaders.clear();
  }
}
